// /apps/patient/src/pages/JoinPage.tsx
// Public landing page for a referral link: /join/:code[?routine=upper|lower|full]
// Validates the code, remembers it for signup, and explains what happens next.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Activity, CheckCircle, AlertCircle } from 'lucide-react';
import { patientApiService } from '../services/api';
import { getEnvironmentConfig } from '../config/platforms';

const ROUTINE_LABELS: Record<string, string> = {
  full: 'Full Body Starter',
  upper: 'Upper Body Starter',
  lower: 'Lower Body Starter',
};

export function JoinPage() {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const config = getEnvironmentConfig();

  const [loading, setLoading] = useState(true);
  const [referral, setReferral] = useState<{ valid: boolean; label?: string; default_routine_key?: string | null; grants_access?: boolean } | null>(null);

  const routineParam = searchParams.get('routine');
  const routineKey = routineParam && ROUTINE_LABELS[routineParam] ? routineParam : (referral?.default_routine_key || null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!code) {
        setReferral({ valid: false });
        setLoading(false);
        return;
      }
      const result = await patientApiService.getReferralCode(code);
      if (cancelled) return;
      setReferral(result);
      if (result?.valid) {
        sessionStorage.setItem('pending_referral_code', code.toLowerCase());
        const key = routineParam && ROUTINE_LABELS[routineParam] ? routineParam : result.default_routine_key;
        if (key) {
          sessionStorage.setItem('pending_routine_key', key);
        } else {
          sessionStorage.removeItem('pending_routine_key');
        }
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [code, routineParam]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!referral?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center">
          <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">This link is not active</h1>
          <p className="text-gray-600 mb-6">Check the link you were given, or create an account on your own.</p>
          <Link to="/signup" className="inline-block px-5 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90">
            Create an account
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8">
        <div className="flex items-center justify-center mb-6">
          <div className="p-3 rounded-full bg-primary/10">
            <Activity className="h-8 w-8 text-primary" />
          </div>
        </div>
        <h1 className="text-2xl font-semibold text-gray-900 text-center mb-2">
          {referral.label ? `Welcome from ${referral.label}` : `Welcome to ${config.appName || 'Ready2Exercise'}`}
        </h1>
        <p className="text-gray-600 text-center mb-6">
          Short home workouts built for where you are right now. A chair, a wall and a few minutes are all you need.
        </p>

        <ul className="space-y-3 mb-8">
          <li className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
            <span className="text-gray-700">
              {routineKey
                ? `You will start on the ${ROUTINE_LABELS[routineKey]} routine, three sessions a week.`
                : 'Answer a few questions and we will build a plan around your goals and equipment.'}
            </span>
          </li>
          <li className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
            <span className="text-gray-700">Every exercise shows a short animation and simple cues.</span>
          </li>
          <li className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
            <span className="text-gray-700">
              {referral.grants_access ? 'Your access is included. No payment step.' : 'Three short sessions a week, at your own pace.'}
            </span>
          </li>
        </ul>

        {routineKey ? (
          <p className="text-center text-sm mb-4">
            <Link to={`/routine/${routineKey}?ref=${encodeURIComponent(code || '')}`} className="text-primary font-medium">
              Preview this routine
            </Link>
          </p>
        ) : (
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">Start with a simple routine</p>
            <div className="grid grid-cols-3 gap-2">
              {(['full', 'upper', 'lower'] as const).map(key => (
                <Link
                  key={key}
                  to={`/routine/${key}?ref=${encodeURIComponent(code || '')}`}
                  className="px-3 py-2 text-sm text-center bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {ROUTINE_LABELS[key].replace(' Starter', '')}
                </Link>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">Each one is three short sessions a week. Preview before you decide.</p>
          </div>
        )}

        <button
          onClick={() => navigate('/signup')}
          className="w-full px-5 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90"
        >
          {routineKey ? 'Get started' : 'Build my own plan'}
        </button>
        <p className="text-center text-sm text-gray-500 mt-4">
          Already have an account? <Link to="/login" className="text-primary font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
